/**
 * @file services/ocular_analysis/cameraFreeze.ts
 * @description Step 25 (14 Oct 2026): recognise a camera that stands still, and record it.
 *
 * Found on 13 Oct (ASMT-1791891801775-4iext, Windows laptop): right after calibration the camera
 * delivered no new image for the remaining 17 minutes. The new-frame gate then measures every
 * screen refresh (its fallback for browsers without requestVideoFrameCallback), so the same still
 * image was measured 18–55 times per second: the same face, the same eye position, the same gaze
 * point. Every task got a gaze that never moved, and nothing in the app or the report said so.
 *
 * A live webcam never delivers two identical images: sensor noise and compression always change
 * some pixels. So each image is reduced to a 32×24 fingerprint and compared with the previous one.
 * - An identical image carries no new information and is not measured (no gaze sample).
 * - No changed image for `freezeMs` means the camera stands still: an episode starts, the app
 *   restarts the camera (backgroundVisionService) and the participant sees a short notice.
 * - A dark image (camera covered) is not a freeze: it never shows a face either way.
 */

export const CAMERA_FREEZE_SETTINGS = {
  version: 'camera-freeze-1',
  /** No changed camera image for this long: the camera stands still. */
  freezeMs: 1000,
  /** A pause between loop ticks longer than this means nobody was watching (hidden tab, loop stopped). */
  maxTickGapMs: 500,
  fingerprintWidth: 32,
  fingerprintHeight: 24,
  /** Mean brightness (0–255) below which the image is dark (camera covered), not frozen. */
  darkLuma: 12,
  /** Restart attempts per episode before giving up. */
  maxRestarts: 3,
  /** After a restart, wait this long for a changed image before the next attempt. */
  restartWaitMs: 2500,
} as const;

export type FrameCallbackState = 'firing' | 'silent' | 'unsupported';

/** What the video looked like when the freeze was found, to learn the cause later. */
export interface CameraFreezeDiagnosis {
  /** requestVideoFrameCallback still announced new images (camera sends the same picture) or went quiet. */
  frameCallback: FrameCallbackState;
  videoPaused: boolean | null;
  /** False when the video element was taken out of the page (browsers pause it then). */
  videoInPage: boolean | null;
  trackState: 'live' | 'ended' | 'none';
  trackMuted: boolean | null;
}

export type CameraFreezeOutcome = 'ongoing' | 'recovered' | 'restart-failed';

export interface CameraFreezeEpisode {
  /** Epoch ms of the last changed image before the freeze. */
  startedAt: number;
  /** Epoch ms the freeze was recognised (startedAt + freezeMs or a little later). */
  detectedAt: number;
  /** Epoch ms of the first changed image afterwards; null while it lasts. */
  endedAt: number | null;
  /** Route of the page where it was recognised. */
  path: string;
  restarts: number;
  outcome: CameraFreezeOutcome;
  diagnosis: CameraFreezeDiagnosis;
}

export interface CameraFreezeRecord {
  version: typeof CAMERA_FREEZE_SETTINGS.version;
  episodes: CameraFreezeEpisode[];
  /** Total time the camera stood still (ms), open episodes counted up to now. */
  frozenMs: number;
  /** Identical images that were not measured. */
  sameImagesSkipped: number;
}

/** The video's image reduced to 32×24 RGBA; null when it cannot be read (not ready, no canvas). */
export class ImageFingerprintReader {
  private ctx: CanvasRenderingContext2D | null | undefined;

  read(video: HTMLVideoElement): Uint8ClampedArray | null {
    if (!video || video.readyState < 2 || typeof document === 'undefined') return null;
    try {
      if (this.ctx === undefined) {
        const canvas = document.createElement('canvas');
        canvas.width = CAMERA_FREEZE_SETTINGS.fingerprintWidth;
        canvas.height = CAMERA_FREEZE_SETTINGS.fingerprintHeight;
        this.ctx = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D | null;
      }
      if (!this.ctx) return null;
      const { fingerprintWidth: w, fingerprintHeight: h } = CAMERA_FREEZE_SETTINGS;
      this.ctx.drawImage(video, 0, 0, w, h);
      return this.ctx.getImageData(0, 0, w, h).data;
    } catch {
      return null;
    }
  }
}

const sameImage = (a: Uint8ClampedArray, b: Uint8ClampedArray): boolean => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
};

const meanLuma = (fp: Uint8ClampedArray): number => {
  let sum = 0;
  for (let i = 0; i < fp.length; i += 4) sum += 0.299 * (fp[i] ?? 0) + 0.587 * (fp[i + 1] ?? 0) + 0.114 * (fp[i + 2] ?? 0);
  return sum / (fp.length / 4);
};

export type CameraFreezeChange = 'froze' | 'thawed';

/**
 * Follows whether the camera image still changes. `isNewImage` is asked for every image the frame
 * gate lets through; `tick` runs on every loop turn and reports when a freeze starts or ends.
 */
export class CameraFreezeWatch {
  private lastImage: Uint8ClampedArray | null = null;
  private lastImageDark = false;
  private lastChangeAt: number | null = null;
  private lastTickAt: number | null = null;
  private current: CameraFreezeEpisode | null = null;
  private episodes: CameraFreezeEpisode[] = [];
  private skipped = 0;

  /** False when the image equals the previous one: it holds nothing new and is not measured. */
  isNewImage(image: Uint8ClampedArray | null, now: number): boolean {
    if (!image) {
      // Cannot judge (video not ready, canvas unavailable): measure as before, but never call it frozen.
      if (!this.current) this.lastChangeAt = now;
      return true;
    }
    if (this.lastImage && sameImage(image, this.lastImage)) {
      this.skipped++;
      return false;
    }
    this.lastImage = image.slice();
    this.lastImageDark = meanLuma(image) < CAMERA_FREEZE_SETTINGS.darkLuma;
    this.lastChangeAt = now;
    return true;
  }

  tick(now: number, path: string, diagnose: () => CameraFreezeDiagnosis): CameraFreezeChange | null {
    const gap = this.lastTickAt === null ? 0 : now - this.lastTickAt;
    this.lastTickAt = now;
    if (gap > CAMERA_FREEZE_SETTINGS.maxTickGapMs && !this.current) {
      // Nobody was watching during the gap; start counting again.
      this.lastChangeAt = now;
      return null;
    }
    if (this.current) {
      if (this.lastChangeAt !== null && this.lastChangeAt > this.current.detectedAt) {
        this.current.endedAt = this.lastChangeAt;
        this.current.outcome = 'recovered';
        this.current = null;
        return 'thawed';
      }
      return null;
    }
    if (this.lastChangeAt === null || this.lastImageDark) return null;
    if (now - this.lastChangeAt < CAMERA_FREEZE_SETTINGS.freezeMs) return null;
    this.current = {
      startedAt: this.lastChangeAt, detectedAt: now, endedAt: null, path,
      restarts: 0, outcome: 'ongoing', diagnosis: diagnose(),
    };
    this.episodes.push(this.current);
    return 'froze';
  }

  isFrozen(): boolean { return this.current !== null; }

  noteRestart(): void { if (this.current) this.current.restarts++; }

  noteRestartFailed(): void { if (this.current) this.current.outcome = 'restart-failed'; }

  getRecord(now: number = Date.now()): CameraFreezeRecord {
    return {
      version: CAMERA_FREEZE_SETTINGS.version,
      episodes: this.episodes.map(e => ({ ...e, diagnosis: { ...e.diagnosis } })),
      frozenMs: this.episodes.reduce((s, e) => s + Math.max(0, (e.endedAt ?? now) - e.startedAt), 0),
      sameImagesSkipped: this.skipped,
    };
  }
}

/** Episodes before a page reload (restored draft) plus those of the running page. */
export function mergeCameraFreezeRecords(
  restored: CameraFreezeRecord | null | undefined,
  live: CameraFreezeRecord | null | undefined,
): CameraFreezeRecord | null {
  if (!restored) return live ?? null;
  if (!live) return restored;
  return {
    version: CAMERA_FREEZE_SETTINGS.version,
    episodes: [...restored.episodes, ...live.episodes],
    frozenMs: restored.frozenMs + live.frozenMs,
    sameImagesSkipped: restored.sameImagesSkipped + live.sameImagesSkipped,
  };
}
